"use client";

import Image from "next/image";
import { INTERVIEWERS, interviewerThumbnailSrc, type InterviewerKey } from "@/lib/interviewers";

interface Props {
  value: InterviewerKey;
  onChange: (key: InterviewerKey) => void;
  disabled?: boolean;
}

/**
 * 面接官の選択カード（#1863）。DESIGN.md §4「選択カード（Radio Card）」に沿う。
 *
 * サムネイルは静止画を使い、開始画面で複数の動画を同時再生しない（iOS の同時動画セッション数の
 * 上限に近づけないため）。面接モード固有の依存を持たせず、後続でクイック面接からも使えるようにする。
 */
export default function InterviewerSelector({ value, onChange, disabled = false }: Props) {
  return (
    <div role="radiogroup" aria-label="面接官" className="grid grid-cols-2 gap-3">
      {INTERVIEWERS.map((interviewer) => {
        const isSelected = interviewer.key === value;
        return (
          <button
            key={interviewer.key}
            type="button"
            role="radio"
            aria-checked={isSelected}
            aria-label={interviewer.label}
            disabled={disabled}
            onClick={() => onChange(interviewer.key)}
            className={`relative flex flex-col overflow-hidden rounded-xl bg-white text-left transition-all ${
              isSelected
                ? "border-2 border-primary"
                : "border border-border hover:border-primary/50"
            } ${disabled ? "opacity-60" : ""}`}
          >
            <div className="relative aspect-video w-full bg-gray-100">
              <Image
                src={interviewerThumbnailSrc(interviewer)}
                alt=""
                fill
                sizes="(min-width: 768px) 320px, 50vw"
                className="object-cover"
                draggable={false}
              />
            </div>
            <div className="px-3 py-2">
              <span
                className={`block text-sm font-bold ${
                  isSelected ? "text-primary" : "text-on-surface"
                }`}
              >
                {interviewer.label}
              </span>
            </div>
          </button>
        );
      })}
    </div>
  );
}
